import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import { EntityFields } from 'src/UI/dynamicUI/types';
import { fieldsToHide } from 'src/data-capture/merge-review/ReviewHelperFunctions';
import { useDeleteEntities } from 'src/data-capture/merge-review/useDeleteEntities';
import { useUpsertEntity } from 'src/data-capture/merge-review/useUpsertEntities';
import {
  DetectionState,
  EntityCategory,
  getDetectionsByCategorySelector,
  getPayloadForNewDetectionSelector,
  getPayloadForUpdateDetectionSelector,
  selectDetectionPayloadsForCategory,
  selectDetectionPayloadsForEntityType,
  selectDetectionSelector,
  selectExistingFields,
  selectLastAction,
  useDetectedEntitiesStore,
} from 'src/data-capture/stores/useDetectedEntitiesStore';
import { Detection } from 'src/data-capture/types';
import { Logger } from 'src/utils/Logger';
import { useInvalidateDetections } from 'src/data-capture/hooks/file-uploads/useDetections';
import { useState } from 'react';

export const entityFieldsToFilterOut = {
  entityType: 'PersonalDetails',
  fieldNames: ['first_name', 'last_name'],
};

export function useUpsertCategoryDetections(category: EntityCategory) {
  const setResolved = useDetectedEntitiesStore(state => state.setCategoryResolved);
  const setCategoryInsertLastActions = useDetectedEntitiesStore(
    state => state.setCategoryInsertLastActions,
  );
  const { upsertEntities } = useUpsertEntity();
  const [isLoading, setIsLoading] = useState(false);

  const upsertCategoryDetections = async (clientId: string) => {
    setIsLoading(true);
    const payloadsByDetection = selectDetectionPayloadsForCategory(
      useDetectedEntitiesStore.getState(),
      category,
    );
    const detectionsInOrder = Object.keys(payloadsByDetection).sort();
    const payloads = detectionsInOrder.map(detectionId => payloadsByDetection[detectionId]).flat();
    try {
      const { data: ids } = await upsertEntities(
        clientId,
        payloads,
        false,
        entityFieldsToFilterOut,
        false,
      );
      const idsByDetectionId = detectionsInOrder.reduce((acc, detectionId, index) => {
        return { ...acc, [detectionId]: ids[index] };
      }, {});
      setResolved(true, category);
      setCategoryInsertLastActions(category, idsByDetectionId);
    } catch (e) {
      setResolved(false, category);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    upsertCategoryDetections,
    isLoading,
  };
}

export function useUpsertEntityTypeDetections(category: EntityCategory, entityType: string) {
  const setResolved = useDetectedEntitiesStore(state => state.setEntitiesOfTypeResolved);
  const setEntityOfTypeInsertLastAction = useDetectedEntitiesStore(
    state => state.setEntityOfTypeInsertLastAction,
  );
  const { upsertEntities } = useUpsertEntity();

  const upsertEntityTypeDetections = async (clientId: string) => {
    const payloadsByDetection = selectDetectionPayloadsForEntityType(
      useDetectedEntitiesStore.getState(),
      category,
      entityType,
    );
    const detectionsInOrder = Object.keys(payloadsByDetection).sort();

    const payloads = detectionsInOrder.map(detectionId => payloadsByDetection[detectionId]).flat();

    try {
      const { data: ids } = await upsertEntities(
        clientId,
        payloads,
        false,
        entityFieldsToFilterOut,
        false,
      );

      const idsByDetectionId = detectionsInOrder.reduce((acc, detectionId, index) => {
        return { ...acc, [detectionId]: ids[index] };
      }, {});

      setEntityOfTypeInsertLastAction(category, entityType, idsByDetectionId);
      setResolved(true, category, entityType);
    } catch (e) {
      setResolved(false, category, entityType);
      throw e;
    }
  };

  return {
    upsertEntityTypeDetections,
  };
}

export default function useUpsertDetectedEntity(
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
) {
  const { upsertEntity, isLoading } = useUpsertEntity();
  const lastAction = useDetectedEntitiesStore(state =>
    selectLastAction(state, category, detectionId),
  );
  const setLastAction = useDetectedEntitiesStore(state => state.setLastAction);
  const selectUpsertPayload = useDetectedEntitiesStore(state =>
    getPayloadForNewDetectionSelector(state),
  );
  const selectUpsertPayloadForUpdate = useDetectedEntitiesStore(state =>
    getPayloadForUpdateDetectionSelector(state),
  );
  const existingFields = useDetectedEntitiesStore(state =>
    selectExistingFields(state, category, detectionId),
  );
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);

  const upsertDetection = async (clientId: string, detectionId: string) => {
    const upsertPayloadForUpdate = selectUpsertPayloadForUpdate(category, detectionId, fieldName);
    if (!upsertPayloadForUpdate) {
      throw new Error('Failed upserting item');
    }

    try {
      await upsertEntity(clientId, upsertPayloadForUpdate, false, entityFieldsToFilterOut);
      setResolved(true, category, detectionId, fieldName);
      setLastAction(
        category,
        detectionId,
        {
          type: 'update',
          targetEntityFieldsMetadata: upsertPayloadForUpdate.entityFieldsMetadata,
          targetEntityType: upsertPayloadForUpdate.entityType,
          previousValues: getPreviousValues(
            (upsertPayloadForUpdate.values as EntityFields)['id']?.toString(),
          ),
        },
        fieldName,
      );
    } catch (e) {
      // Restore the last action that already existed
      setResolved(false, category, detectionId, fieldName);
      setLastAction(category, detectionId, lastAction ?? null);
      throw e;
    }
  };

  const getPreviousValues = (id?: string) => {
    if (fieldName) {
      const previousField = existingFields.find(f => f.key === fieldName);
      return { [fieldName]: previousField?.value || null };
    }

    return existingFields
      .filter(({ key }) => !fieldName || key === fieldName)
      .reduce(
        (acc, currentValue) => {
          return { ...acc, ...{ [currentValue.key]: currentValue.value } };
        },
        id ? { id } : {},
      );
  };

  const insertDetectionEntity = async (
    clientId: string,
    category: EntityCategory,
    detectionId: string,
  ) => {
    const upsertPayload = selectUpsertPayload(category, detectionId, fieldName);
    if (!upsertPayload) {
      return;
    }

    try {
      const upsertedId = await upsertEntity(
        clientId,
        upsertPayload,
        false,
        entityFieldsToFilterOut,
      );

      if (!upsertedId) {
        Logger.error(
          'BUG! Inserted new detection entity but no ID was returned',
          {},
          { detectionId, clientId },
        );
        return;
      }

      setResolved(true, category, detectionId);
      setLastAction(category, detectionId, {
        type: 'insert',
        targetEntityFieldsMetadata: upsertPayload.entityFieldsMetadata,
        targetEntityType: upsertPayload.entityType,
        insertedEntityId: upsertedId,
      });
    } catch (e) {
      setResolved(false, category, detectionId, fieldName);
      throw e;
    }
  };

  return {
    upsertDetection,
    insertDetectionEntity,
    isLoading,
  };
}

export function useUndoMutation() {
  // const { refetch } = useFileUploadWithEntities();
  const { invalidateDetections } = useInvalidateDetections();
  const selectDetection = useDetectedEntitiesStore(selectDetectionSelector);
  const { deleteEntityByTypeAndId } = useDeleteEntities();
  const setResolved = useDetectedEntitiesStore(state => state.setResolved);
  const { upsertEntity } = useUpsertEntity();
  const setLastAction = useDetectedEntitiesStore(state => state.setLastAction);
  const setExistingValues = useDetectedEntitiesStore(state => state.setExistingValues);
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);

  const undoDetectionLastAction = async (
    clientId: string,
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
    warnWhenNoLastAction: boolean = true,
  ) => {
    const detectionState = selectDetection(category, detectionId);

    Logger.info('undoDetectionLastAction started', {
      category,
      clientId,
      detectionId,
      fieldName,
      currentLastAction: detectionState?.lastAction ?? null,
    });

    if (
      detectionState == null ||
      detectionState?.isDiscarded ||
      (fieldName && detectionState?.fieldsByKey[fieldName]?.isDiscarded)
    ) {
      setDiscarded(false, category, detectionId, {
        fieldNames: fieldName ? [fieldName] : undefined,
      });
      return;
    }

    const lastAction = detectionState.lastAction;
    if (lastAction === null) {
      if (warnWhenNoLastAction) {
        notifyAndWarn(
          'Nothing to undo',
          'No action found to undo. Contact support if the issue persists.',
          {
            detectionId,
            clientId,
            fieldName,
          },
        );
      }

      return;
    }

    try {
      if (lastAction.type === 'insert') {
        const { targetEntityType, insertedEntityId } = lastAction;
        await deleteEntityByTypeAndId(clientId, targetEntityType, insertedEntityId);
        setResolved(false, category, detectionId);
        Logger.info('undoDetectionLastAction calling setLastAction (insert)', {
          detectionId,
          fieldName,
          lastActionType: 'insert',
        });
        setLastAction(category, detectionId, null, fieldName);
      }

      if (lastAction.type === 'update') {
        const { targetEntityType, targetEntityFieldsMetadata, previousValues } = lastAction;
        if (fieldName && !(fieldName in previousValues)) {
          notifyAndWarn(
            'Nothing to undo',
            'No action found to undo. Contact support if the issue persists.',
            {
              detectionId,
              clientId,
              fieldName,
            },
          );
          return;
        }

        const values = fieldName ? { [fieldName]: previousValues[fieldName] } : previousValues;

        const upsertPayload = {
          entityType: targetEntityType,
          entityFieldsMetadata: targetEntityFieldsMetadata,
          values,
        };
        await upsertEntity(clientId, upsertPayload, true, entityFieldsToFilterOut);
        setExistingValues(category, detectionId, values);
        Logger.info('undoDetectionLastAction calling setLastAction (update)', {
          detectionId,
          fieldName,
          lastActionType: 'update',
        });
        setLastAction(category, detectionId, null, fieldName);
        setResolved(false, category, detectionId, fieldName);
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to undo',
        'We were unable to undo this action. Please try again or contact support.',
        e,
        { detectionId, clientId, fieldName },
      );
      await invalidateDetections();
    }
  };

  return {
    undoDetectionLastAction,
  };
}

export function useUndoAllMutation() {
  const selectDetectionsByCategory = useDetectedEntitiesStore(getDetectionsByCategorySelector);
  const { undoDetectionLastAction } = useUndoMutation();
  const setDiscarded = useDetectedEntitiesStore(state => state.setDiscarded);
  const selectDetection = useDetectedEntitiesStore(selectDetectionSelector);

  async function undoAllInCategory(clientId: string, category: EntityCategory) {
    await undoAllDetections(clientId, category, selectDetectionsByCategory(category), false);
  }

  async function undoAllInCategoryAndEntityType(
    clientId: string,
    category: EntityCategory,
    entityType: string,
    warnWhenNoLastAction: boolean,
  ) {
    await undoAllDetections(
      clientId,
      category,
      selectDetectionsByCategory(category, entityType),
      warnWhenNoLastAction,
    );
  }

  async function undoAllDetections(
    clientId: string,
    category: EntityCategory,
    detections: DetectionState[],
    warnWhenNoLastAction: boolean,
  ) {
    Logger.info('undoAllDetections started', {
      category,
      clientId,
      numDetections: detections.length,
      detectionIds: detections.map(d => d.id),
      detectionsWithMultipleFields: detections
        .filter(d => !d.isConflictable)
        .map(d => ({
          id: d.id,
          numFields: Object.keys(d.fieldsByKey).length,
          lastActionType: d.lastAction?.type ?? null,
        })),
    });

    await Promise.all(
      detections.flatMap(detection => {
        if (detection.isConflictable) {
          return undoDetectionLastAction(
            clientId,
            category,
            detection.id,
            undefined,
            warnWhenNoLastAction,
          );
        }

        const detectionState = selectDetection(category, detection.id);
        const discardedFields = Object.values(detectionState?.fieldsByKey ?? {})
          .filter(field => field.isDiscarded)
          .map(field => field.key);

        if (detectionState?.isDiscarded) {
          setDiscarded(false, category, detection.id);
        } else if (discardedFields.length > 0) {
          setDiscarded(false, category, detection.id, { fieldNames: discardedFields });
        }

        const fields = Object.keys(detection.fieldsByKey)
          .filter(f => !fieldsToHide.includes(f) && detection.fieldsByKey[f].value !== null)
          .filter(f => !discardedFields.includes(f));

        return fields.map(field =>
          undoDetectionLastAction(clientId, category, detection.id, field, warnWhenNoLastAction),
        );
      }),
    );
  }
  return { undoAllInCategory, undoAllInCategoryAndEntityType };
}
